/**
 * The phone's shared parts.
 *
 * These are the pieces the time-tracker got right and this app kept rebuilding
 * slightly differently on each screen: a 48pt field, a 48pt button, a project
 * row you tap anywhere on, and a section label with a rule running off it.
 */

import type { CSSProperties, ReactNode } from "react";

/* -- Styles ----------------------------------------------------------------- */

/** 48pt is the tap-target floor; anything shorter is a miss waiting to happen. */
export const field: CSSProperties = {
  width: "100%", minHeight: 48, padding: "10px 12px", borderRadius: 10, fontSize: 15,
  border: "1px solid var(--line2)", background: "var(--surface)", color: "var(--ink)",
};

export const tapPrimary: CSSProperties = {
  minHeight: 48, padding: "0 16px", borderRadius: 10, border: "none",
  background: "var(--accent)", color: "var(--onAccent)",
  fontSize: 15, fontWeight: 600, cursor: "pointer",
};

export const tapGhost: CSSProperties = {
  minHeight: 48, padding: "0 12px", borderRadius: 10,
  border: "1px solid var(--line2)", background: "var(--surface)", color: "var(--ink2)",
  fontSize: 14, cursor: "pointer",
};

/** Screen padding, with room under the last row for the tab bar and its button. */
export const screen: CSSProperties = {
  padding: "12px 14px calc(var(--above-tabbar) + 84px)",
};

/* -- Parts ------------------------------------------------------------------ */

/** A label with a rule running off it, and an optional figure at the end. */
export function SectionLabel({ children, right }: { children: ReactNode; right?: ReactNode }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "18px 2px 6px" }}>
      <span style={{
        fontSize: "var(--fs-eyebrow, 11px)", letterSpacing: "var(--ls-eyebrow, 0.06em)",
        textTransform: "uppercase", color: "var(--muted2)", flex: "none",
      }}>{children}</span>
      <span style={{ flex: 1, height: 1, background: "var(--line)" }} />
      {right !== undefined && (
        <span className="mono" style={{ flex: "none", fontSize: 11.5, color: "var(--muted2)" }}>{right}</span>
      )}
    </div>
  );
}

/**
 * A switch inside a screen.
 *
 * Buckets are the tab bar's job; this is what a bucket is divided into. Four
 * is the ceiling at 375pt, and only with one-word labels — each slot is about
 * 88px, so "Items" fits and "Client report" would not.
 */
export function Segmented<T extends string>({
  value, onChange, options,
}: {
  value: T;
  onChange: (next: T) => void;
  options: { value: T; label: string }[];
}) {
  return (
    <div
      role="tablist"
      style={{
        display: "flex", gap: 3, padding: 3, borderRadius: 10,
        background: "var(--tint)", border: "1px solid var(--line)",
      }}
    >
      {options.map((o) => {
        const on = o.value === value;
        return (
          <button
            key={o.value}
            role="tab"
            aria-selected={on}
            onClick={() => onChange(o.value)}
            style={{
              flex: 1, minHeight: 38, borderRadius: 8, border: "none", cursor: "pointer",
              background: on ? "var(--surface)" : "transparent",
              color: on ? "var(--ink)" : "var(--muted)",
              fontSize: 13.5, fontWeight: on ? 600 : 400,
              boxShadow: on ? "0 1px 2px rgba(0,0,0,0.08)" : "none",
              WebkitTapHighlightColor: "transparent",
            }}
          >{o.label}</button>
        );
      })}
    </div>
  );
}

/**
 * The project picker, as a row rather than a dropdown.
 *
 * A bare `<select>` on a phone shows one truncated line and nothing else — no
 * colour, no client, no task. This shows all of it and puts a transparent
 * native select across the whole row, so the tap still opens the system picker
 * (which is the good part: it is a full-height wheel, already familiar, and
 * handles a hundred projects without a scroll container of our own).
 */
export function PickerRow({
  value, onChange, options, color, primary, secondary, placeholder = "Pick a project", label,
}: {
  value: string;
  onChange: (next: string) => void;
  options: { value: string; label: string }[];
  color?: string;
  primary?: string;
  secondary?: string;
  placeholder?: string;
  label: string;
}) {
  return (
    <div style={{ position: "relative", display: "flex", alignItems: "center", gap: 11, minHeight: 52 }}>
      <span style={{
        width: 10, height: 10, borderRadius: 3, flex: "none",
        background: color || "var(--muted3)",
      }} />
      <span style={{ flex: 1, minWidth: 0 }}>
        <span style={{
          display: "block", fontSize: 15, color: primary ? "var(--ink)" : "var(--muted)",
          overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
        }}>{primary || placeholder}</span>
        {secondary && (
          <span style={{
            display: "block", fontSize: 12, color: "var(--muted2)",
            overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
          }}>{secondary}</span>
        )}
      </span>
      <span aria-hidden style={{ flex: "none", color: "var(--muted2)", fontSize: 13 }}>▾</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        aria-label={label}
        style={{ position: "absolute", inset: 0, width: "100%", height: "100%", opacity: 0 }}
      >
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </div>
  );
}

/** Duration as people say it: 45m, 2h, 1h30m. */
export function durationLabel(minutes: number): string {
  const h = Math.floor(minutes / 60);
  const m = Math.round(minutes % 60);
  if (h && m) return `${h}h${m}m`;
  if (h) return `${h}h`;
  return `${m}m`;
}
